import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  TextField,
  IconButton,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { Video, Copy, VideoOff } from "lucide-react";
import { useSendVideoInvitation, useUpdateVideoRoom } from "services/video";
import { useGetFamilyDetails } from "services/advisor";
import { createLocalVideoTrack } from "livekit-client";
import { LocalUserChoices } from "@livekit/components-react";
import PreJoinScreen from "./PreJoinScreen";
import { Family } from "protogen/advisors_service_pb";
import useIsMobile from "components/hooks/useIsMobile";
import VideoButton from "./VideoButton";
import MicrophoneButton from "./MicrophoneButton";
import Checkbox from "components/common/Checkbox";

interface VideoCallSetupProps {
  videoRoomUuid: string;
  familyRef?: string;
  onJoin: (choices: LocalUserChoices) => void;
}

const VideoCallSetup: React.FC<VideoCallSetupProps> = ({
  videoRoomUuid,
  familyRef,
  onJoin,
}) => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [selectedInviteOption, setSelectedInviteOption] = useState<string>("");
  const [meetingLink, setMeetingLink] = useState<string>("");
  const [transcribe, setTranscribe] = useState<boolean>(true);
  const [isSendingInvite, setIsSendingInvite] = useState<boolean>(false);
  const [showPreJoin, setShowPreJoin] = useState<boolean>(false);
  const [videoEnabled, setVideoEnabled] = useState<boolean>(true);
  const [audioEnabled, setAudioEnabled] = useState<boolean>(true);
  const [localVideoTrack, setLocalVideoTrack] =
    useState<MediaStreamTrack | null>(null);
  const [family, setFamily] = useState<Family | null>(null);
  const [selectedAudioDevice, setSelectedAudioDevice] = useState<string>("");
  const [selectedVideoDevice, setSelectedVideoDevice] = useState<string>("");
  const [audioDevices, setAudioDevices] = useState<MediaDeviceInfo[]>([]);
  const [videoDevices, setVideoDevices] = useState<MediaDeviceInfo[]>([]);

  const { request: sendVideoInvitation } = useSendVideoInvitation();
  const { request: getFamilyDetails } = useGetFamilyDetails();
  const { request: updateVideoRoom } = useUpdateVideoRoom();

  useEffect(() => {
    if (videoRoomUuid) {
      const meetingUrl = `${window.location.origin}/video/${videoRoomUuid}`;
      setMeetingLink(meetingUrl);
    }
  }, [videoRoomUuid]);

  useEffect(() => {
    if (familyRef) {
      const fetchFamily = async () => {
        const response = await getFamilyDetails({ familyRef });
        if (response?.family) {
          setFamily(response.family);
        }
      };
      fetchFamily();
    }
  }, [familyRef]);

  useEffect(() => {
    const initializeMedia = async () => {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoInputs = devices.filter(
        (device) => device.kind === "videoinput",
      );
      const audioInputs = devices.filter(
        (device) => device.kind === "audioinput",
      );

      setVideoDevices(videoInputs);
      setAudioDevices(audioInputs);

      if (videoInputs.length > 0) {
        setSelectedVideoDevice(videoInputs[0].deviceId);
        const videoTrack = await createLocalVideoTrack({
          deviceId: videoInputs[0].deviceId,
        });
        setLocalVideoTrack(videoTrack.mediaStreamTrack);
      }

      if (audioInputs.length > 0) {
        setSelectedAudioDevice(audioInputs[0].deviceId);
      }
    };

    initializeMedia();
    return () => {
      if (localVideoTrack) {
        localVideoTrack.stop();
      }
    };
  }, []);

  const handleInviteClient = async () => {
    if (!selectedInviteOption || !videoRoomUuid) return;

    const [memberRef, mediumType] = selectedInviteOption.split("|");
    if (!memberRef || !mediumType) return;

    setIsSendingInvite(true);
    await sendVideoInvitation({
      videoRoomUuid,
      memberRef,
      medium: mediumType,
    });

    setIsSendingInvite(false);
  };

  const handleCopyLink = () => {
    if (meetingLink) {
      navigator.clipboard.writeText(meetingLink);
    }
  };

  const handleVideoDeviceChange = async (deviceId: string) => {
    setSelectedVideoDevice(deviceId);
    if (localVideoTrack) {
      localVideoTrack.stop();
    }
    const videoTrack = await createLocalVideoTrack({ deviceId });
    setLocalVideoTrack(videoTrack.mediaStreamTrack);
  };

  const handleAudioDeviceChange = (deviceId: string) => {
    setSelectedAudioDevice(deviceId);
  };

  const handleStartCall = async () => {
    await updateVideoRoom({
      uuid: videoRoomUuid,
      transcriptionEnabled: transcribe,
    });

    if (selectedInviteOption && family) {
      await handleInviteClient();
    }

    const choices: LocalUserChoices = {
      username: "User",
      audioEnabled,
      videoEnabled,
      audioDeviceId: selectedAudioDevice,
      videoDeviceId: selectedVideoDevice,
    };

    onJoin(choices);
  };

  const handleJoinFromPreJoin = (choices: LocalUserChoices) => {
    onJoin(choices);
  };

  if (showPreJoin) {
    return (
      <Box
        sx={{
          position: "relative",
          height: "100vh",
          backgroundColor: "#232323",
        }}
      >
        <PreJoinScreen containerized onJoin={handleJoinFromPreJoin} />
        <Button
          onClick={() => setShowPreJoin(false)}
          variant="text"
          sx={{
            position: "absolute",
            top: 16,
            right: 16,
            color: "white",
            zIndex: 1000,
          }}
        >
          Back
        </Button>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: isMobile ? "column" : "row",
        maxWidth: isMobile ? "100%" : "1200px",
      }}
    >
      <Box
        sx={{
          flex: isMobile ? "0 0 300px" : 1,
          backgroundColor: "#232323",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
          minHeight: isMobile ? "300px" : "auto",
        }}
      >
        <Box
          sx={{
            width: isMobile ? "280px" : "400px",
            height: isMobile ? "210px" : "300px",
            backgroundColor: "#232323",
            borderRadius: "12px",
            overflow: "hidden",
            position: "relative",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          {videoEnabled && localVideoTrack ? (
            <video
              ref={(videoElement) => {
                if (videoElement && localVideoTrack) {
                  videoElement.srcObject = new MediaStream([localVideoTrack]);
                }
              }}
              autoPlay
              muted
              playsInline
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
            />
          ) : (
            <Box sx={{ textAlign: "center", color: "white" }}>
              <VideoOff size={64} />
              <Typography variant="body1" sx={{ mt: 1 }}>
                Camera is off
              </Typography>
            </Box>
          )}
        </Box>

        {/* Controls overlay */}
        <Box
          sx={{
            position: "absolute",
            bottom: "20%",
            left: "50%",
            transform: "translateX(-50%)",
            display: "flex",
            gap: 2,
            backgroundColor: "rgba(0, 0, 0, 0.7)",
            borderRadius: "24px",
            padding: "8px 16px",
          }}
        >
          <MicrophoneButton
            enabled={audioEnabled}
            onToggle={() => setAudioEnabled(!audioEnabled)}
            devices={audioDevices}
            activeDeviceId={selectedAudioDevice}
            onDeviceChange={handleAudioDeviceChange}
          />
          <VideoButton
            enabled={videoEnabled}
            onToggle={() => setVideoEnabled(!videoEnabled)}
            devices={videoDevices}
            activeDeviceId={selectedVideoDevice}
            onDeviceChange={handleVideoDeviceChange}
          />
        </Box>
      </Box>

      <Box
        sx={{
          width: isMobile ? "100%" : "400px",
          padding: isMobile ? "0px 24px" : "0px 32px",
          display: "flex",
          flexDirection: "column",
          flex: isMobile ? 1 : "0 0 400px",
          gap: "16px",
          justifyContent: "space-between",
        }}
      >
        <Box>
          <Typography variant="bodyHeavy">Invite client</Typography>
          {family && (
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Send invitation to...</InputLabel>
              <Select
                value={selectedInviteOption}
                label="Send invitation to..."
                onChange={(e) =>
                  setSelectedInviteOption(e.target.value as string)
                }
                sx={{ borderRadius: "8px" }}
              >
                <MenuItem value="">
                  <em>No invitation</em>
                </MenuItem>
                {family?.familyMembers
                  .map((member) => {
                    const options = [];

                    if (member.primaryEmail) {
                      options.push(
                        <MenuItem
                          value={`${member.ref}|email`}
                          key={`${member.ref}-email`}
                        >
                          {member.displayName} (via email)
                        </MenuItem>,
                      );
                    }

                    if (member.primaryPhone) {
                      options.push(
                        <MenuItem
                          value={`${member.ref}|text`}
                          key={`${member.ref}-text`}
                        >
                          {member.displayName} (via text)
                        </MenuItem>,
                      );
                    }

                    return options;
                  })
                  .flat()}
              </Select>
            </FormControl>
          )}
        </Box>
        <Box>
          <Typography variant="bodyHeavy">Share the meeting link</Typography>
          <TextField
            fullWidth
            value={meetingLink}
            InputProps={{
              readOnly: true,
              endAdornment: (
                <IconButton onClick={handleCopyLink}>
                  <Copy size={20} />
                </IconButton>
              ),
              sx: { borderRadius: "8px", input: { marginTop: "0px" } },
            }}
          />
        </Box>

        <FormControlLabel
          control={
            <Checkbox
              checked={transcribe}
              onChange={(e) => setTranscribe(e.target.checked)}
            />
          }
          label="Transcribe the call for task creation"
        />

        <Box sx={{ display: "flex", gap: "8px" }}>
          <Button
            fullWidth
            variant="contained"
            color="primary"
            onClick={handleStartCall}
            disabled={isSendingInvite}
            startIcon={<Video size={20} />}
          >
            {isSendingInvite ? "Sending invitation..." : "Start call"}
          </Button>
          <Button
            fullWidth
            variant="outlinedSecondary"
            onClick={() => navigate("/")}
          >
            Cancel
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default VideoCallSetup;
